import React, { useState, useEffect } from 'react';
import { useConfirm } from '../shared/AppDialogs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Section component for grouping settings
const SettingsSection = ({ title, icon, children, description }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
      <div className="flex items-center gap-3">
        <span className="text-xl">{icon}</span>
        <div>
          <h3 className="font-semibold text-gray-800">{title}</h3>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>
      </div>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

// Toggle switch component
const Toggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-start justify-between gap-4">
    <div>
      <div className="font-medium text-gray-700">{label}</div>
      {description && <div className="text-sm text-gray-500">{description}</div>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-emerald-500' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

// Number input component
const NumberInput = ({ label, description, value, onChange, min, max, step = 1, unit }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex-1">
      <div className="font-medium text-gray-700">{label}</div>
      {description && <div className="text-sm text-gray-500">{description}</div>}
    </div>
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
        className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      />
      {unit && <span className="text-sm text-gray-500 w-8">{unit}</span>}
    </div>
  </div>
);

// Select input component
const SelectInput = ({ label, description, value, onChange, options }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex-1">
      <div className="font-medium text-gray-700">{label}</div>
      {description && <div className="text-sm text-gray-500">{description}</div>}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// Text input for phrases (comma-separated)
const PhrasesInput = ({ label, description, value, onChange, placeholder }) => (
  <div>
    <div className="font-medium text-gray-700 mb-1">{label}</div>
    {description && <div className="text-sm text-gray-500 mb-2">{description}</div>}
    <textarea
      value={Array.isArray(value) ? value.join(', ') : value}
      onChange={(e) => onChange(e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(Boolean))}
      placeholder={placeholder}
      rows={2}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
    />
  </div>
);

// Text input for prompts
const PromptInput = ({ label, description, value, onChange, placeholder }) => (
  <div>
    <div className="font-medium text-gray-700 mb-1">{label}</div>
    {description && <div className="text-sm text-gray-500 mb-2">{description}</div>}
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
    />
  </div>
);

export function VoiceSettingsPanel() {
  const confirm = useConfirm();
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.data);
        setDefaults(data.defaults);
      } else {
        setError(data.detail || 'Fehler beim Laden');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.data);
        setSuccess(true);
        setHasChanges(false);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(data.detail || 'Fehler beim Speichern');
      }
    } catch (err) {
      setError('Verbindungsfehler: ' + err.message);
    }
    setSaving(false);
  };

  const resetToDefaults = async () => {
    const ok = await confirm({ title: 'Zurücksetzen', message: 'Alle Einstellungen auf Standard zurücksetzen?', confirmText: 'Zurücksetzen', type: 'warning' });
    if (!ok) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.data);
        setHasChanges(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      setError('Fehler: ' + err.message);
    }
    setSaving(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const applyPreset = (preset) => {
    const presets = {
      natural: {
        vad_mode: 'semantic_vad',
        semantic_vad_eagerness: 'low',
        allow_interruptions: true,
        echo_cooldown_ms: 300,
        silence_timeout_seconds: 30
      },
      fast: {
        vad_mode: 'semantic_vad',
        semantic_vad_eagerness: 'high',
        allow_interruptions: true,
        echo_cooldown_ms: 200,
        silence_timeout_seconds: 20
      },
      precise: {
        vad_mode: 'server_vad',
        allow_interruptions: false,
        echo_cooldown_ms: 1200,
        silence_timeout_seconds: 45
      }
    };
    
    setSettings(prev => ({ ...prev, ...presets[preset] }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12 text-red-500">
        {error || 'Einstellungen konnten nicht geladen werden'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Spracheinstellungen</h2>
          <p className="text-gray-500">Systemweite Einstellungen - Stabile Konfiguration</p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
              Ungespeicherte Änderungen
            </span>
          )}
          <button
            onClick={resetToDefaults}
            disabled={saving}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Zurücksetzen
          </button>
          <button
            onClick={saveSettings}
            disabled={saving || !hasChanges}
            className={`px-5 py-2 rounded-lg font-medium transition-all ${
              hasChanges
                ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Quick Presets */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
        <h3 className="font-medium text-indigo-800 mb-3">🚀 Schnell-Setup</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => applyPreset('natural')}
            className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm font-medium transition-colors"
          >
            🧠 Natürlicher Dialog (empfohlen)
          </button>
          <button
            onClick={() => applyPreset('fast')}
            className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
          >
            ⚡ Schnelle Antworten
          </button>
          <button
            onClick={() => applyPreset('precise')}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
          >
            🎯 Präzise (keine Unterbrechungen)
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg">
          ✓ Einstellungen gespeichert
        </div>
      )}

      {/* Settings Sections */}
      <div className="grid gap-6">
        
        {/* VAD Mode - Primary Setting */}
        <SettingsSection 
          title="Turn-Taking (VAD)" 
          icon="🧠"
          description="Wie erkennt der Agent wann Sie fertig sind?"
        >
          <SelectInput
            label="VAD Modus"
            description="semantic = KI versteht Satzende, server = wartet auf Stille"
            value={settings.vad_mode}
            onChange={(v) => updateSetting('vad_mode', v)}
            options={[
              { value: 'semantic_vad', label: '🧠 Semantic VAD (empfohlen)' },
              { value: 'server_vad', label: '⏱️ Server VAD (klassisch)' }
            ]}
          />
          
          {settings.vad_mode === 'semantic_vad' && (
            <SelectInput
              label="Reaktionsgeschwindigkeit"
              description="low = geduldig, high = schnell"
              value={settings.semantic_vad_eagerness}
              onChange={(v) => updateSetting('semantic_vad_eagerness', v)}
              options={[
                { value: 'low', label: '🐢 Geduldig (komplexe Gespräche)' },
                { value: 'medium', label: '⚖️ Ausgewogen' },
                { value: 'high', label: '🐇 Schnell (FAQ)' },
                { value: 'auto', label: '🤖 Auto' }
              ]}
            />
          )}
          
          <Toggle
            label="Unterbrechungen erlauben"
            description="Sie können den Agent während des Sprechens unterbrechen"
            checked={settings.allow_interruptions}
            onChange={(v) => updateSetting('allow_interruptions', v)}
          />
          
          <NumberInput
            label="Echo-Cooldown"
            description="Kurze Pause nach Agent-Sprache"
            value={settings.echo_cooldown_ms}
            onChange={(v) => updateSetting('echo_cooldown_ms', v)}
            min={100}
            max={1000}
            step={50}
            unit="ms"
          />
        </SettingsSection>

        {/* Timeouts */}
        <SettingsSection 
          title="Timeouts" 
          icon="⏱️"
          description="Wann der Anruf automatisch beendet wird"
        >
          <NumberInput
            label="Stille-Timeout"
            description="Auflegen nach X Sekunden ohne Antwort"
            value={settings.silence_timeout_seconds}
            onChange={(v) => updateSetting('silence_timeout_seconds', v)}
            min={10}
            max={120}
            unit="s"
          />
          <NumberInput
            label="Max. Anrufdauer"
            description="Maximale Länge eines Anrufs"
            value={settings.max_call_duration_seconds}
            onChange={(v) => updateSetting('max_call_duration_seconds', v)}
            min={60}
            max={1800}
            unit="s"
          />
          <NumberInput
            label="Verabschiedungs-Pause"
            description="Wartezeit nach Goodbye"
            value={settings.goodbye_grace_period_seconds}
            onChange={(v) => updateSetting('goodbye_grace_period_seconds', v)}
            min={1}
            max={10}
            unit="s"
          />
        </SettingsSection>

        {/* Side Conversation */}
        <SettingsSection 
          title="Nebengespräch-Erkennung" 
          icon="🗣️"
          description="Erkennt wenn Sie kurz mit jemand anderem sprechen"
        >
          <Toggle
            label="Aktivieren"
            description="Agent wartet still bei erkanntem Nebengespräch"
            checked={settings.side_conversation_enabled}
            onChange={(v) => updateSetting('side_conversation_enabled', v)}
          />
          {settings.side_conversation_enabled && (
            <>
              <PhrasesInput
                label="Erkennungs-Phrasen"
                description="Kommagetrennt"
                value={settings.side_conversation_phrases}
                onChange={(v) => updateSetting('side_conversation_phrases', v)}
                placeholder="schatz, moment mal, ja mama..."
              />
              <NumberInput
                label="Warte-Zeit"
                description="Stille Wartezeit vor Nachfrage"
                value={settings.side_conversation_wait_seconds}
                onChange={(v) => updateSetting('side_conversation_wait_seconds', v)}
                min={2}
                max={30}
                unit="s"
              />
              <PromptInput
                label="Nachfrage"
                value={settings.side_conversation_prompt}
                onChange={(v) => updateSetting('side_conversation_prompt', v)}
                placeholder="Sind Sie noch da?"
              />
            </>
          )}
        </SettingsSection>

        {/* Hold Feature */}
        <SettingsSection 
          title="Hold/Pause-Funktion" 
          icon="⏸️"
          description="Sie können den Agent kurz auf 'Hold' setzen"
        >
          <Toggle
            label="Aktivieren"
            description="Erkennt 'Moment bitte' und wartet"
            checked={settings.hold_feature_enabled}
            onChange={(v) => updateSetting('hold_feature_enabled', v)}
          />
          {settings.hold_feature_enabled && (
            <>
              <PhrasesInput
                label="Hold-Trigger"
                value={settings.hold_trigger_phrases}
                onChange={(v) => updateSetting('hold_trigger_phrases', v)}
                placeholder="moment bitte, kurz warten..."
              />
              <PhrasesInput
                label="Resume-Trigger"
                value={settings.hold_resume_phrases}
                onChange={(v) => updateSetting('hold_resume_phrases', v)}
                placeholder="bin wieder da, ja, weiter..."
              />
              <NumberInput
                label="Hold-Timeout"
                description="Max. Wartezeit"
                value={settings.hold_timeout_seconds}
                onChange={(v) => updateSetting('hold_timeout_seconds', v)}
                min={10}
                max={120}
                unit="s"
              />
              <PromptInput
                label="Timeout-Nachfrage"
                value={settings.hold_timeout_prompt}
                onChange={(v) => updateSetting('hold_timeout_prompt', v)}
                placeholder="Hallo? Sind Sie noch da?"
              />
            </>
          )}
        </SettingsSection>

        {/* Server VAD Settings (only when server_vad selected) */}
        {settings.vad_mode === 'server_vad' && (
          <SettingsSection 
            title="Server VAD Parameter" 
            icon="🎤"
            description="Nur bei Server VAD Modus"
          >
            <NumberInput
              label="Schwellenwert"
              description="0 = sensitiv, 1 = unsensitiv"
              value={settings.default_vad_threshold}
              onChange={(v) => updateSetting('default_vad_threshold', v)}
              min={0}
              max={1}
              step={0.1}
            />
            <NumberInput
              label="Stille-Dauer"
              description="Wie lange Stille bevor Antwort"
              value={settings.default_vad_silence_duration_ms}
              onChange={(v) => updateSetting('default_vad_silence_duration_ms', v)}
              min={200}
              max={2000}
              step={50}
              unit="ms"
            />
          </SettingsSection>
        )}

      </div>
    </div>
  );
}
